<template>
  <div>
    <h1>Dashboard</h1>
    <div class="d-flex mb-3">
      <div class="mr-2">
        <autocomplete-input
          :path="'/distributors?withProject=1&project=name&project=_id&fields=name&fields=name&ascending=1&orderBy=name'"
          name="Select distributor(s)"
          label="name"
          multiple
          loadOnFocus
          placeholder="Select distributor(s)"
          isObject
          track-by="_id"
          v-model="selectedFilter.distributorId"
        />
      </div>

      <DateRange :dateRange="dashboardDateRange" :showRefresh="true" @onChange="dateRangeSelected"></DateRange>
    </div>
    <div class="row">
      <div class="col-md-8">
        <div class="card mb-4">
          <div class="card-body">
            <div class="card-title">
              <div class="row">
                <div class="col">
                  <h5>Sales</h5>
                  <h3>{{ myReports.sales.total || 0 | currency(distributorCurrencySign) }}</h3>
                </div>
                <div class="col">
                  <h5 class="font-weight-normal">Net Profit</h5>
                  <h3 class="font-weight-normal">
                    {{ (myReports.salesTotals.profit || 0) | currency(distributorCurrencySign) }}
                  </h3>
                </div>
                <div class="col">
                  <h5 class="font-weight-normal">Gross Margin</h5>
                  <h3 class="font-weight-normal">{{ (myReports.salesTotals.grossMargin || 0) | percent }}</h3>
                </div>
                <div class="col">
                  <h5 class="font-weight-normal">Net Margin</h5>
                  <h3 class="font-weight-normal">{{ (myReports.salesTotals.netMargin || 0) | percent }}</h3>
                </div>
              </div>
            </div>
            <div v-if="myReports.sales.data">
              <line-chart
                :height="440"
                :isCurrency="true"
                :data="myReports.sales.data.map((i) => ({ x: i.label, y: i.total }))"
                :period="dashboardDateRange.short"
              />
            </div>
            <vue-content-loading v-else>
              <rect x="0" y="0" rx="4" ry="4" :height="150" class="w-100" />
            </vue-content-loading>
          </div>
        </div>
      </div>

      <div class="col-md-4">
        <div class="card mb-4">
          <div class="card-body">
            <div class="card-title">
              <h5>Orders</h5>
            </div>
            <div v-if="myReports.orders.data">
              <h3>{{ myReports.orders.total }}</h3>
              <line-chart
                :height="150"
                :data="myReports.orders.data.map((i) => ({ x: i.label, y: i.count }))"
                :period="dashboardDateRange.short"
              />
            </div>
            <vue-content-loading v-else>
              <rect x="0" y="0" rx="4" ry="4" :height="100" class="w-100" />
            </vue-content-loading>
          </div>
        </div>

        <div class="card mb-4">
          <div class="card-body">
            <div class="card-title">
              <h5>Net Profit</h5>
            </div>
            <div v-if="myReports.profit.data">
              <h3>{{ myReports.profit.total | currency(distributorCurrencySign) }}</h3>
              <line-chart
                :height="150"
                :isCurrency="true"
                :data="myReports.profit.data.map((i) => ({ x: i.label, y: i.profit }))"
                :period="dashboardDateRange.short"
              />
            </div>
            <vue-content-loading v-else>
              <rect x="0" y="0" rx="4" ry="4" :height="100" class="w-100" />
            </vue-content-loading>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-6">
        <div class="card mb-4">
          <div class="card-body">
            <div class="card-title">
              <div class="row">
                <div class="col">
                  <h5>Sales</h5>
                  <h3>{{ getOrdersAndSalesTotal('totalPrice') | currency(distributorCurrencySign) }}</h3>
                </div>
              </div>
            </div>
            <div>
              <div v-if="loadedReports || myReports.ordersAndSalesByDistributor.length">
                <bar-chart
                  :height="600"
                  :isCurrency="true"
                  :data="sortedBarData('ordersAndSalesByDistributor', 'totalPrice')"
                />
              </div>
              <vue-content-loading v-else>
                <rect x="0" y="0" rx="4" ry="4" :height="300" class="w-100" />
              </vue-content-loading>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6">
        <div class="card mb-4">
          <div class="card-body">
            <div class="card-title">
              <h5>Orders</h5>
              <h3>{{ getOrdersAndSalesTotal('totalCount') }}</h3>
            </div>
            <div v-if="loadedReports || myReports.ordersAndSalesByDistributor.length">
              <bar-chart :height="600" :data="sortedBarData('ordersAndSalesByDistributor', 'totalCount')" />
            </div>
            <vue-content-loading v-else>
              <rect x="0" y="0" rx="4" ry="4" :height="300" class="w-100" />
            </vue-content-loading>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-md-4">
        <div class="card mb-4">
          <div class="card-body">
            <div class="card-title">
              <h5>Quotes</h5>
              <h3 class="mr-2">{{ getStatusByDistributorCount('quotesByDistributor') }}</h3>
            </div>
            <div v-if="loaded || myReports.quotesByDistributor.length">
              <bar-chart :height="300" :data="sortedBarData('quotesByDistributor', 'totalCount')" />
            </div>
            <vue-content-loading v-else>
              <rect x="0" y="0" rx="4" ry="4" :height="100" class="w-100" />
            </vue-content-loading>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card mb-4">
          <div class="card-body">
            <div class="card-title">
              <h5>Proofs</h5>
              <h3 class="mr-2">{{ getStatusByDistributorCount('proofsByDistributor') }}</h3>
            </div>
            <div v-if="loadedReports || myReports.proofsByDistributor.length">
              <bar-chart :height="300" :data="sortedBarData('proofsByDistributor', 'totalCount')" />
            </div>
            <vue-content-loading v-else>
              <rect x="0" y="0" rx="4" ry="4" :height="100" class="w-100" />
            </vue-content-loading>
          </div>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card mb-4">
          <div class="card-body">
            <div class="card-title">
              <h5>Customers</h5>
              <h3>{{ getCustomersTotal('totalCount') }}</h3>
            </div>
            <div v-if="loadedReports || myReports.customersCountByDistributor.length">
              <bar-chart :height="300" :data="sortedBarData('customersCountByDistributor', 'totalCount')" />
            </div>
            <vue-content-loading v-else>
              <rect x="0" y="0" rx="4" ry="4" :height="150" class="w-100" />
            </vue-content-loading>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapMutations } from 'vuex';
import { getDistributors } from '@/api/distributors';

const DEFAULT_REPORTS_OBJECT = {
  sales: {},
  salesTotals: {},
  orders: {},
  profit: {},
  ordersAndSalesByDistributor: [],
  customersCountByDistributor: [],
  quotesByDistributor: [],
  proofsByDistributor: []
};

export default {
  data() {
    return {
      filterTab: null,
      selectedFilter: {
        distributorId: []
      },
      distributors: [],
      myReports: { ...DEFAULT_REPORTS_OBJECT },
      doughnutOptions: { responsive: true, maintainAspectRatio: false, legend: { position: 'bottom' } },
      loadedReports: false
    };
  },
  watch: {
    async 'selectedFilter.distributorId'() {
      await this.getReports(this.dashboardDateRange);
    }
  },

  methods: {
    ...mapMutations({
      setDataRange: 'SET_DASHBOARD_DATE_RANGE'
    }),
    getStatusByDistributorCount(field) {
      return this.myReports[field].map((i) => i.totalCount).reduce((a, b) => a + b, 0);
    },
    getOrdersAndSalesTotal(field) {
      return this.myReports.ordersAndSalesByDistributor.map((i) => i[field]).reduce((a, b) => a + b, 0);
    },
    getCustomersTotal(field) {
      return this.myReports.customersCountByDistributor.map((i) => i[field]).reduce((a, b) => a + b, 0);
    },
    sortedBarData(dataName, field) {
      const reports = this.myReports[dataName].map((i) => ({ x: i[field], y: i.name }));
      return _.orderBy(reports, 'x', ['desc']);
    },
    async dateRangeSelected(range) {
      await this.getReports(range);
      this.setDataRange(range);
    },
    async getReports(dateRange) {
      this.loadedReports = false;
      this.myReports = { ...DEFAULT_REPORTS_OBJECT };
      const context = this;
      console.log(context.selectedFilter);
      function asyncFetch(body) {
        Object.assign(body, { dateRange });
        let distributorId;
        if (context.selectedFilter.distributorId.length) {
          distributorId = context.selectedFilter.distributorId.map((e) => e._id || e);
        } else {
          distributorId = context.distributors.map((i) => i._id);
        }
        Object.assign(body, { distributorId });
        return context.http.post('/reports/dashboard', body).then(({ data }) => Object.assign(context.myReports, data));
      }
      const asyncThingsToDo = Object.keys(DEFAULT_REPORTS_OBJECT).map((i) => ({ [i]: true }));
      const reports = asyncThingsToDo.map((i) => asyncFetch(i));
      const data = await Promise.all(reports);
      this.loadedReports = true;
      return data;
    }
  },
  computed: {
    ...mapGetters(['dashboardDateRange'])
  },
  filters: {
    startCase: function (value) {
      if (!value) return '';
      return _.startCase(value);
    }
  },
  components: {
    LineChart: () => import('@/components/Charts/LineChart'),
    MultipleLineChart: () => import('@/components/Charts/MultipleLineChart'),
    BarChart: () => import('@/components/Charts/BarChart'),
    Doughnut: () => import('@/components/Charts/Doughnut.vue'),
    StackedBarChart: () => import('@/components/Charts/StackedBarChart'),
    DateRange: () => import('@/components/DateRange'),
    TopTableSingleFilter: () => import('@/components/TopTableSingleFilter')
  }
};
</script>
